import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Container,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';

const SolicitudCotizacionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = Boolean(id);

  // Form state
  const [requisiciones, setRequisiciones] = useState([]);
  const [requisicionId, setRequisicionId] = useState('');
  const [lugarEntrega, setLugarEntrega] = useState('');
  const [comentariosNotas, setComentariosNotas] = useState('');
  const [estado, setEstado] = useState('Borrador');
  const [moneda, setMoneda] = useState('MXN'); // Global currency for all articles
  const [detalles, setDetalles] = useState([
    { id: uuidv4(), articulo: '', cantidad: 1, unidad: '', precio_unitario: null },
  ]);
  const [impuestos, setImpuestos] = useState([]);

  // Supplier state
  const [proveedores, setProveedores] = useState([]);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [proveedorTemporalNombre, setProveedorTemporalNombre] = useState('');

  // Control state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [reqResponse, provResponse] = await Promise.all([
          axios.get('/api/requisiciones'),
          axios.get('/api/proveedores'),
        ]);
        setRequisiciones(reqResponse.data);
        setProveedores(provResponse.data);
      } catch (err) {
        setError('No se pudieron cargar los datos iniciales.');
        console.error(err);
      }
      setLoading(false);
    };

    fetchInitialData();
  }, []); // Empty dependency array ensures this runs only once on mount

  useEffect(() => {
    if (isEditMode) {
      const fetchSolicitud = async () => {
        if (proveedores.length > 0) { // Ensure providers are loaded
          try {
            setLoading(true);
            const response = await axios.get(`/api/solicitudes_cotizacion/${id}`);
            const { requisicion_id, lugar_entrega, comentarios_notas, estado, detalles, proveedor, impuestos: solImpuestos } = response.data;
            setRequisicionId(requisicion_id);
            setLugarEntrega(lugar_entrega || '');
            setComentariosNotas(comentarios_notas || '');
            setEstado(estado);
            // Extract moneda from first detalle
            if (detalles && detalles.length > 0 && detalles[0].moneda) {
              setMoneda(detalles[0].moneda);
            }
            // Keep moneda on detalles for backend compatibility
            setDetalles(detalles.map(d => ({ ...d, id: uuidv4(), precio_unitario: d.precio_unitario || null })));
            // Load impuestos or use default
            if (solImpuestos && solImpuestos.length > 0) {
              setImpuestos(solImpuestos.map(imp => ({ ...imp, id: uuidv4() })));
            }
            if (proveedor) {
              const fullProveedor = proveedores.find(p => p.id === proveedor.id);
              setSelectedProveedor(fullProveedor || null);
            }
            setLoading(false);
          } catch (err) {
            setError('No se pudo cargar la solicitud para editar.');
            console.error(err);
            setLoading(false);
          }
        }
      };
      fetchSolicitud();
    } else if (location.state?.requisicionId) {
      setRequisicionId(location.state.requisicionId);
      // Inherit currency from first detalle
      if (location.state.detalles && location.state.detalles.length > 0 && location.state.detalles[0].moneda) {
        setMoneda(location.state.detalles[0].moneda);
      }
      // Keep moneda on detalles when inheriting from REQ
      setDetalles(location.state.detalles.map(d => ({ ...d, id: uuidv4(), unidad: d.unidad || '', precio_unitario: d.precio || null })));
      // Inherit taxes from requisicion
      if (location.state.impuestos && location.state.impuestos.length > 0) {
        setImpuestos(location.state.impuestos.map(imp => ({ ...imp, id: uuidv4() })));
      }
    }
  }, [id, isEditMode, location.state, proveedores]);

  // Calculate totals
  const calculatedTotals = useMemo(() => {
    const subtotal = detalles.reduce((acc, d) => {
      const precio = parseFloat(d.precio_unitario) || 0;
      const cantidad = parseFloat(d.cantidad) || 0;
      return acc + (precio * cantidad);
    }, 0);

    // Use the monto from impuestos state directly (no auto-recalculation)
    const totalImpuestos = impuestos.reduce((acc, imp) => acc + (parseFloat(imp.monto) || 0), 0);
    const total = subtotal + totalImpuestos;

    return { subtotal, totalImpuestos, total };
  }, [detalles, impuestos]);

  const handleDetalleChange = (detalleId, field, value) => {
    const newDetalles = detalles.map((d) => {
      if (d.id === detalleId) {
        return { ...d, [field]: value };
      }
      return d;
    });
    setDetalles(newDetalles);
  };

  const addDetalle = () => {
    setDetalles([
      ...detalles,
      { id: uuidv4(), articulo: '', cantidad: 1, unidad: '', precio_unitario: null },
    ]);
  };

  const removeDetalle = (detalleId) => {
    setDetalles(detalles.filter((d) => d.id !== detalleId));
  };

  const handleImpuestoChange = (impuestoId, field, value) => {
    const subtotal = detalles.reduce((acc, d) => {
      const precio = parseFloat(d.precio_unitario) || 0;
      const cantidad = parseFloat(d.cantidad) || 0;
      return acc + (precio * cantidad);
    }, 0);

    const newImpuestos = impuestos.map((imp) => {
      if (imp.id === impuestoId) {
        const updatedImpuesto = { ...imp, [field]: value };

        // If porcentaje changes, auto-calculate monto (one-way) and round to 10 decimals
        if (field === 'porcentaje') {
          const calculated = subtotal * (parseFloat(value) / 100);
          updatedImpuesto.monto = Math.round(calculated * 10000000000) / 10000000000;
        }
        // If monto changes, don't touch porcentaje

        return updatedImpuesto;
      }
      return imp;
    });
    setImpuestos(newImpuestos);
  };

  const addImpuesto = () => {
    setImpuestos([
      ...impuestos,
      { id: uuidv4(), nombre: '', porcentaje: 0, monto: 0 },
    ]);
  };

  const removeImpuesto = (impuestoId) => {
    setImpuestos(impuestos.filter((imp) => imp.id !== impuestoId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Frontend validation: Proveedor is mandatory
    if (!selectedProveedor && !proveedorTemporalNombre) {
      setError('Debe seleccionar un proveedor existente o crear uno temporal.');
      return;
    }

    const { subtotal, totalImpuestos, total } = calculatedTotals;

    const cleanImpuestos = impuestos.map(({ id, ...rest }) => ({
      ...rest,
      porcentaje: parseFloat(rest.porcentaje) || 0,
      monto: parseFloat(rest.monto) || 0
    }));

    const solicitudData = {
      requisicion_id: requisicionId,
      lugar_entrega: lugarEntrega,
      comentarios_notas: comentariosNotas,
      estado,
      subtotal,
      iva: totalImpuestos,  // For backward compatibility
      total,
      impuestos: cleanImpuestos,
      detalles: detalles.map(({ id, ...rest }) => ({
        ...rest,
        unidad: rest.unidad || null,
        precio_unitario: rest.precio_unitario || null,
        moneda: moneda, // Set global moneda on each detalle
      })),
      proveedor_id: selectedProveedor ? selectedProveedor.id : null,
      proveedor_temporal_nombre: proveedorTemporalNombre,
    };

    try {
        if (isEditMode) {
            await axios.put(`/api/solicitudes_cotizacion/${id}`, solicitudData);
            navigate(`/solicitudes_cotizacion/${id}`);
        } else {
            // The backend will generate the ID for the RFQ
            await axios.post('/api/solicitudes_cotizacion', solicitudData);
            navigate('/solicitudes_cotizacion', { state: { refresh: true } });
        }
    } catch (err) {
      setError('No se pudo guardar la solicitud. Verifique los datos.');
      console.error(err);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          {isEditMode ? 'Editar Solicitud de Cotización' : 'Crear Nueva Solicitud de Cotización'}
        </Typography>
        <form onSubmit={handleSubmit}>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Requisición</InputLabel>
            <Select value={requisicionId} label="Requisición" onChange={(e) => setRequisicionId(e.target.value)} disabled={Boolean(location.state?.requisicionId) || isEditMode}>
              {requisiciones.map((requisicion) => (
                <MenuItem key={requisicion.id} value={requisicion.id}>
                  {`Requisición #${requisicion.id.substring(0, 8)}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Proveedor *
          </Typography>

          <Autocomplete
            options={proveedores}
            getOptionLabel={(option) => option.nombre_fiscal || ''}
            value={selectedProveedor}
            onChange={(event, newValue) => {
              setSelectedProveedor(newValue);
              if (newValue) {
                setProveedorTemporalNombre(''); // Clear temporary name if a provider is selected
              }
            }}
            renderInput={(params) => <TextField {...params} label="Seleccionar Proveedor Existente" />}
            disabled={!!proveedorTemporalNombre} // Disable if temporary name is being used
            fullWidth
            margin="normal"
          />

        <Typography align="center" sx={{ my: 1 }}>O</Typography>

          <TextField
            label="Crear Proveedor Temporal (solo nombre)"
            value={proveedorTemporalNombre}
            onChange={(e) => {
              setProveedorTemporalNombre(e.target.value);
              if (e.target.value) {
                setSelectedProveedor(null); // Clear selected provider if a temporary name is being typed
              }
            }}
            disabled={!!selectedProveedor} // Disable if a provider is selected
            fullWidth
            margin="normal"
          />

          <TextField
            label="Lugar de Entrega"
            value={lugarEntrega}
            onChange={(e) => setLugarEntrega(e.target.value)}
            fullWidth
            margin="normal"
            sx={{ mt: 3 }}
          />
          <TextField
            label="Comentarios o Notas especiales"
            value={comentariosNotas}
            onChange={(e) => setComentariosNotas(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />

          {isEditMode && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Estado</InputLabel>
              <Select value={estado} label="Estado" onChange={(e) => setEstado(e.target.value)}>
                <MenuItem value="Borrador">Borrador</MenuItem>
                <MenuItem value="Enviada">Enviada</MenuItem>
                <MenuItem value="Respondida">Respondida</MenuItem>
                <MenuItem value="Cerrada">Cerrada</MenuItem>
              </Select>
            </FormControl>
          )}

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Artículos
          </Typography>

          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              select
              label="Moneda"
              value={moneda}
              onChange={(e) => setMoneda(e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="MXN">MXN</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
            </TextField>
          </Box>

          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '40%' }}>Artículo</TableCell>
                  <TableCell>Cantidad</TableCell>
                  <TableCell>Unidad</TableCell>
                  <TableCell>Precio Unitario</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detalles.map((detalle) => (
                  <TableRow key={detalle.id}>
                    <TableCell>
                      <TextField
                        value={detalle.articulo}
                        onChange={(e) => handleDetalleChange(detalle.id, 'articulo', e.target.value)}
                        required
                        fullWidth
                        variant="standard"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={detalle.cantidad}
                        onChange={(e) => handleDetalleChange(detalle.id, 'cantidad', parseFloat(e.target.value) || 1)}
                        required
                        variant="standard"
                        inputProps={{ step: "0.0000000001", min: "0" }}
                        sx={{ width: '80px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={detalle.unidad}
                        onChange={(e) => handleDetalleChange(detalle.id, 'unidad', e.target.value)}
                        placeholder="ej. PZA"
                        variant="standard"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={detalle.precio_unitario || ''}
                        onChange={(e) => handleDetalleChange(detalle.id, 'precio_unitario', e.target.value ? parseFloat(e.target.value) : null)}
                        inputProps={{ step: "0.0000000001", min: "0" }}
                        InputProps={{
                          startAdornment: moneda === 'EUR' ? '€' : '$',
                        }}
                        variant="standard"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => removeDetalle(detalle.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button startIcon={<AddIcon />} onClick={addDetalle}>
            Añadir Artículo
          </Button>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>Impuestos</Typography>
          {impuestos.map((impuesto) => (
            <Box key={impuesto.id} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                label="Nombre del Impuesto"
                value={impuesto.nombre}
                onChange={(e) => handleImpuestoChange(impuesto.id, 'nombre', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Porcentaje (%)"
                type="number"
                value={impuesto.porcentaje}
                onChange={(e) => handleImpuestoChange(impuesto.id, 'porcentaje', e.target.value)}
                inputProps={{ step: "0.0000000001", min: "0" }}
                sx={{ width: 150 }}
              />
              <TextField
                label="Monto"
                type="number"
                value={impuesto.monto}
                onChange={(e) => handleImpuestoChange(impuesto.id, 'monto', e.target.value)}
                inputProps={{ step: "0.0000000001", min: "0" }}
                sx={{ width: 180 }}
              />
              <IconButton onClick={() => removeImpuesto(impuesto.id)} color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button startIcon={<AddIcon />} onClick={addImpuesto} sx={{ mt: 1 }}>Añadir Impuesto</Button>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}></Grid>
              <Grid item xs={6}>
                <Typography align="right">Subtotal: ${calculatedTotals.subtotal.toFixed(2)}</Typography>
                {impuestos && impuestos.map((impuesto) => (
                  <Typography key={impuesto.id} align="right">
                    {impuesto.nombre} ({impuesto.porcentaje}%): ${(parseFloat(impuesto.monto) || 0).toFixed(2)}
                  </Typography>
                ))}
                <Typography align="right" variant="h6">Total: ${calculatedTotals.total.toFixed(2)}</Typography>
              </Grid>
            </Grid>
          </Box>

          <Button type="submit" variant="contained" color="primary" sx={{ mt: 3, display: 'block' }}>
            {isEditMode ? 'Guardar Cambios' : 'Crear Solicitud'}
          </Button>
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </form>
      </Paper>
    </Container>
  );
};

export default SolicitudCotizacionForm;
